/* =============================================================
   THEME.JS — Présentoirs premium
   Vanilla JS · zero deps · Custom Elements API · ES2020+
   ============================================================= */

(() => {
  'use strict';

  /* ---------- 1. SMALL DOM UTILS ---------- */
  const $  = (sel, scope = document) => scope.querySelector(sel);
  const $$ = (sel, scope = document) => Array.from(scope.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const off = (el, ev, fn, opts) => el && el.removeEventListener(ev, fn, opts);
  const debounce = (fn, wait = 200) => {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
  const throttle = (fn, limit = 100) => {
    let inThrottle = false;
    return function throttled(...args) {
      if (inThrottle) return;
      inThrottle = true;
      fn.apply(this, args);
      setTimeout(() => (inThrottle = false), limit);
    };
  };
  const trapFocus = (root) => {
    const focusable = root.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    on(root, 'keydown', handler);
    first.focus();
    return () => off(root, 'keydown', handler);
  };

  /* ---------- 2. CART API ---------- */
  const Cart = {
    async get() {
      const r = await fetch(`${window.theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      return r.json();
    },
    async add(items) {
      const body = Array.isArray(items) ? { items } : { items: [items] };
      const r = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Cart add failed');
      }
      this.emit();
      return r.json();
    },
    async change(payload) {
      const r = await fetch(window.theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      this.emit();
      return r.json();
    },
    emit() {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  };
  window.theme.Cart = Cart;

  /* ---------- 3. STICKY HEADER ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = 0;
      this.hideThreshold = 200;
      this.onScroll = throttle(() => this.update(), 100);
      on(window, 'scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() { off(window, 'scroll', this.onScroll); }
    update() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 10);
      if (y > this.lastY && y > this.hideThreshold) this.classList.add('is-hidden');
      else this.classList.remove('is-hidden');
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- 4. ACCORDION ---------- */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('data-single');
      $$('.accordion__item', this).forEach((item, idx) => {
        const btn   = $('.accordion__trigger', item);
        const panel = $('.accordion__panel', item);
        if (!btn || !panel) return;
        // Wire ARIA relationship
        if (!btn.id)   btn.id   = `acc-trigger-${Math.random().toString(36).slice(2, 9)}`;
        if (!panel.id) panel.id = `acc-panel-${Math.random().toString(36).slice(2, 9)}`;
        btn.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', btn.id);
        panel.setAttribute('aria-hidden', btn.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
        on(btn, 'click', e => this.toggle(e.currentTarget));
      });
    }
    toggle(btn) {
      const item = btn.closest('.accordion__item');
      const panel = $('.accordion__panel', item);
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      if (this.single) {
        $$('.accordion__trigger', this).forEach(b => {
          if (b !== btn) {
            b.setAttribute('aria-expanded', 'false');
            const p = b.closest('.accordion__item').querySelector('.accordion__panel');
            if (p) { p.dataset.open = 'false'; p.setAttribute('aria-hidden', 'true'); }
          }
        });
      }
      btn.setAttribute('aria-expanded', String(!expanded));
      if (panel) { panel.dataset.open = String(!expanded); panel.setAttribute('aria-hidden', String(expanded)); }
    }
  }
  customElements.define('accordion-group', AccordionGroup);

  /* ---------- 5. DRAWER (cart, menu, search) ---------- */
  // Delegated: any element with [data-target] AND [is="drawer-toggle"]
  // (we don't actually register a custom element — `is=` here is just a CSS hook)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-target][is="drawer-toggle"], [data-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    const id = trigger.getAttribute('data-target') || trigger.getAttribute('data-drawer-open');
    if (id) Drawer.open(id);
  });

  const Drawer = {
    _release: null,
    _activeId: null,
    open(id) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      this.close();
      let backdrop = $('.drawer__backdrop');
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.className = 'drawer__backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        on(backdrop, 'click', () => this.close());
        document.body.appendChild(backdrop);
      }
      requestAnimationFrame(() => {
        backdrop.setAttribute('aria-hidden', 'false');
        drawer.setAttribute('aria-hidden', 'false');
      });
      document.body.classList.add('drawer-open');
      this._activeId = id;
      this._release = trapFocus(drawer);
      on(document, 'keydown', this._esc = (e) => { if (e.key === 'Escape') this.close(); });
    },
    close() {
      if (!this._activeId) return;
      const drawer = document.getElementById(this._activeId);
      const backdrop = $('.drawer__backdrop');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (this._release) this._release();
      if (this._esc) off(document, 'keydown', this._esc);
      this._activeId = null;
    }
  };
  window.theme.Drawer = Drawer;

  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-drawer-close]') || e.target.closest('[data-drawer-close]')) {
      Drawer.close();
    }
  });

  /* ---------- 6. PREDICTIVE SEARCH ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input   = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.onInput = debounce(() => this.search(), 220);
      on(this.input, 'input', this.onInput);
      on(this.input, 'focus', () => this.classList.add('is-active'));
      on(document, 'click', (e) => {
        if (!this.contains(e.target)) this.classList.remove('is-active');
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      this.classList.add('is-loading');
      try {
        const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
        const r = await fetch(url);
        if (!r.ok) throw new Error('Predictive search failed');
        const html = await r.text();
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const fragment = tmp.querySelector('[data-predictive-results]') || tmp;
        this.results.innerHTML = fragment.innerHTML;
      } catch (err) {
        this.results.innerHTML = `<p class="predictive-search__error">${window.theme.strings.searchNoResults}</p>`;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- 7. PRODUCT ADD TO CART (delegated) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[data-product-form]')) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-to-cart]');
    if (!btn) return;
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '…';
    try {
      const fd = new FormData(form);
      const json = Object.fromEntries(fd.entries());
      if (json.quantity) json.quantity = Number(json.quantity);
      await Cart.add({ id: Number(json.id), quantity: json.quantity || 1 });
      btn.innerHTML = original;
      btn.disabled = false;
      Drawer.open('CartDrawer');
    } catch (err) {
      console.error('[cart]', err);
      btn.innerHTML = window.theme.strings.unavailable || 'Erreur';
      setTimeout(() => { btn.innerHTML = original; btn.disabled = false; }, 1800);
    }
  });

  /* ---------- 8. QUANTITY INPUT ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    const step = Number(btn.dataset.qtyStep) || 1;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || 999;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + step));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- 9. LAZY HORIZONTAL CAROUSEL (CSS scroll-snap) ---------- */
  class CarouselScroll extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      const prev = $('[data-carousel-prev]', this);
      const next = $('[data-carousel-next]', this);
      if (prev) on(prev, 'click', () => this.scrollBy(-1));
      if (next) on(next, 'click', () => this.scrollBy(1));
    }
    scrollBy(dir) {
      if (!this.track) return;
      const card = this.track.querySelector('[data-carousel-item]');
      const step = (card?.offsetWidth || 320) + 24;
      this.track.scrollBy({ left: step * dir, behavior: 'smooth' });
    }
  }
  customElements.define('carousel-scroll', CarouselScroll);

  /* ---------- 10. FORM VALIDATION HELPER ---------- */
  document.addEventListener('invalid', (e) => {
    const f = e.target;
    if (!f.matches('input, textarea, select')) return;
    f.classList.add('is-invalid');
    on(f, 'input', () => f.classList.remove('is-invalid'), { once: true });
  }, true);

  /* ---------- 11. CART COUNT BADGE refresh ---------- */
  document.addEventListener('cart:updated', async () => {
    try {
      const cart = await Cart.get();
      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.toggleAttribute('hidden', cart.item_count === 0);
      });
    } catch (err) { /* noop */ }
  });

  /* ---------- 12. INITIAL CART COUNT ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  });

})();
